import { Store } from "../store";
import { isAfter, isValid } from "date-fns";
import { safeParseDateString } from "./date-helpers";

/**
 * Check if a date is in the past, relative to the current time.
 *
 * Uses safeParseDateString for consistent date parsing across all formats:
 * - ISO 8601: "2026-01-12T17:18:32"
 * - Space-separated: "2026-01-12 17:18:32" or "2026-01-12 17:18"
 * - Locale format: "1/12/2026, 17:18:32" (M/D/YYYY US format from API responses)
 *
 * @param {string|Date|number} date - The date to check
 * @returns {boolean} True if the date is in the past, false otherwise
 */
export const isDateInThePast = (
  date: string | Date | null | undefined,
  timezone?: string
): boolean => {
  // Only fallback to store if timezone is explicitly undefined (not passed)
  // When called from reducers, timezone should always be passed to avoid Store.getState() during reducer execution
  const userTz =
    timezone !== undefined
      ? timezone
      : (Store.getState() as { user?: { timezone?: string } }).user?.timezone;

  // Handle null/undefined/empty string early - these are not valid dates
  if (date === null || date === undefined || date === "") {
    return false;
  }

  // Parse the date using the consolidated date parser
  const dateObj = safeParseDateString(date, userTz);

  // Check if date is valid before proceeding
  if (!isValid(dateObj)) {
    return false;
  }

  // Compare current time to the parsed date
  // Both are JavaScript Date objects (internally UTC), so direct comparison is valid
  return isAfter(new Date(), dateObj);
};
